/**
 * Screen 1 — Login.
 *
 * A split screen: the teal value panel on the left states what the product is
 * and why it is compulsory, the form sits on white at the right. That is the
 * `Portal · Login` frame, and it is the first thing the client sees at review.
 *
 * DeCA has its own credentials: the client chose a separate login rather than
 * DAC federation (resolved 2026-07-29), so nothing here talks to the DAC
 * backend.
 *
 * A failed sign-in says the same thing whether or not the address exists — the
 * server returns an identical 401 either way, and the screen must not undo that
 * by phrasing the two differently.
 *
 * Two departures from the frame, both deliberate. The **language switch** is not
 * drawn anywhere on it, and both of the portal's other language controls live
 * inside the authenticated shell — so on a contracted bilingual product whose
 * running build is the review vehicle, English would be reachable only by
 * signing in in Spanish, switching, and signing out. The **BOE identifier** is
 * kept under the value panel: the frame's paragraph cites the Resolution by
 * date, and the number is the thing a compliance officer actually looks up.
 */

import { useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { DecaApiError, unwrap } from '@deca/api'

import logoUrl from '../assets/asgtrans-logo.svg'
import { useAuth } from '../app/auth.tsx'
import { apiErrorMessage } from '../app/errors.ts'
import { api } from '../app/api.ts'
import { landingPathFor } from '../deca/navigation.ts'
import { LegalLinks } from '../legal/LegalLinks.tsx'
import { Button, Notice } from '../ui/primitives.tsx'
import { TextInput } from '../ui/form.tsx'
import { Icon } from '../ui/Icon.tsx'
import { LanguagePicker } from '../ui/LanguagePicker.tsx'
import { loginSearchSchema } from './searchSchemas.ts'

const VALUE_POINTS = ['auth.value1', 'auth.value2', 'auth.value3'] as const

export function LoginScreen() {
  const { t } = useTranslation()
  const { signIn } = useAuth()
  const navigate = useNavigate()

  // `useSearch({ strict: false })` + a manual `.parse`, not `route.useSearch()` — the route
  // object lives in `router.tsx`, which imports this component, and importing it back here
  // would be circular. `DecaListScreen` uses the same idiom for the authenticated routes.
  const rawSearch = useSearch({ strict: false })
  const { reset: justReset } = loginSearchSchema.parse(rawSearch)

  const [mode, setMode] = useState<'login' | 'forgot'>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      if (mode === 'login') {
        // Route by the principal `signIn` resolves with, not the one destructured
        // from `useAuth()` above — that one was captured before this call and is
        // still `null`, because the state update that would refresh it has not
        // re-rendered this component yet.
        const signedInPrincipal = await signIn(email, password)
        void navigate({ to: landingPathFor(signedInPrincipal) })
      } else {
        // `unwrap` matters here. Without it `openapi-fetch` resolves with the failure in
        // `{ error }` instead of throwing, and the success notice went up even when the
        // request had failed — which is what a missing endpoint looked like: a green
        // "we have sent you an email" for a mail that could never arrive.
        //
        // Not distinguishing *which* address has an account is the server's job, and it
        // does it by answering 204 either way. That is a different thing from hiding that
        // the request failed at all.
        await unwrap(api.POST('/api/v1/auth/password/forgot', { body: { email } }))
        setSent(true)
      }
    } catch (caught) {
      if (caught instanceof DecaApiError && caught.status === 401) {
        setError(t('auth.invalidCredentials'))
      } else if (caught instanceof DecaApiError && caught.code === 'ACCOUNT_LOCKED') {
        // `/login`'s lockout is 429 code=ACCOUNT_LOCKED (auth.ts), never 423 — nothing in
        // backend/src ever answers 423. Branching on the code, not the status, also keeps this
        // apart from the ordinary per-IP/per-email 429 the rate limiter answers with, which
        // `apiErrorMessage` already renders correctly from `errors.rateLimited`; this account
        // lockout is a fifteen-minute wait, not the limiter's, so it needs its own message.
        setError(t('auth.locked'))
      } else {
        setError(apiErrorMessage(caught, t))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth-screen">
      <aside className="auth-panel">
        <img src={logoUrl} alt={t('app.operator')} className="auth-panel__logo" />
        <h1 className="auth-panel__headline">{t('app.documentTitle')}</h1>
        <p className="auth-panel__intro">{t('auth.valueIntro')}</p>

        <ul className="auth-panel__points">
          {VALUE_POINTS.map((key) => (
            <li key={key}>
              <Icon name="checkCircle" size={18} />
              <span>{t(key)}</span>
            </li>
          ))}
        </ul>

        <p className="auth-panel__legal">{t('app.legalBasis')}</p>
        <LegalLinks className="auth-panel__links" />
      </aside>

      <div className="auth-form-side">
        <div className="auth-form-side__language">
          <LanguagePicker />
        </div>

        <form className="auth-card" onSubmit={onSubmit}>
          <div className="stack-sm">
            <h2 className="auth-card__title">
              {mode === 'login' ? t('auth.loginTitle') : t('auth.forgotTitle')}
            </h2>
            {/* Sign-in carries no subtitle: the value panel beside it has
                already said what the product is. Recovery keeps one, because
                what happens next is not obvious from "Recuperar el acceso". */}
            {mode === 'forgot' ? <p className="small muted">{t('auth.forgotSubtitle')}</p> : null}
          </div>

          {error ? <Notice tone="danger">{error}</Notice> : null}
          {sent ? <Notice tone="success">{t('auth.forgotSent')}</Notice> : null}
          {/* Set by ResetPasswordScreen's navigate on success. Shown whenever the URL
              carries it, independent of `error`/`sent`, which belong to this screen's own
              two actions. */}
          {justReset && mode === 'login' ? <Notice tone="success">{t('reset.done')}</Notice> : null}

          <div className="stack">
            <TextInput
              label={t('auth.email')}
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />

            {mode === 'login' ? (
              <TextInput
                label={t('auth.password')}
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            ) : null}
          </div>

          {/* Above the button, as the frame has it: the way out of a forgotten
              password should be visible before the attempt, not after it. */}
          <button
            type="button"
            className="link-button"
            onClick={() => {
              setMode(mode === 'login' ? 'forgot' : 'login')
              setError(null)
              setSent(false)
            }}
          >
            {mode === 'login' ? t('auth.forgot') : t('auth.backToLogin')}
          </button>

          <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={busy}>
            {mode === 'login' ? t('auth.submit') : t('auth.forgotSubmit')}
          </Button>

          {/* A sub-user genuinely cannot self-register: accounts are created by
              their own company's administrator. */}
          {mode === 'login' ? <p className="auth-card__note">{t('auth.noAccount')}</p> : null}
        </form>
      </div>
    </div>
  )
}
